import { useState, useEffect } from 'react';
import { DayPicker, DateRange as DayPickerDateRange } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './PeriodPicker.css';

// Data collection started December 5, 2025
const DATA_START_DATE = new Date('2025-12-05T00:00:00');

type PresetId = '48h' | '7d' | '30d' | 'custom';

interface Preset {
  id: PresetId;
  label: string;
  daysRequired: number;
  getRange: () => { start: Date; end: Date };
}

const PRESETS: Preset[] = [
  {
    id: '48h',
    label: '48 Hours',
    daysRequired: 2,
    getRange: () => {
      const now = new Date();
      const start = new Date(now);
      start.setHours(now.getHours() - 48);
      return { start, end: now };
    },
  },
  {
    id: '7d',
    label: '7 Days',
    daysRequired: 7,
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    },
  },
  {
    id: '30d',
    label: '30 Days',
    daysRequired: 30,
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    },
  },
];

function toDateTimeLocal(date: Date): string {
  return date.toLocaleString('en-CA', {
    timeZone: 'America/Halifax',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).replace(', ', 'T');
}

function formatDisplayRange(startDate: string, endDate: string): string {
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'Custom';
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${startStr} - ${endStr}`;
  } catch {
    return 'Custom';
  }
}

function getAvailablePresets(): Preset[] {
  const now = new Date();
  const daysOfData = Math.floor((now.getTime() - DATA_START_DATE.getTime()) / (1000 * 60 * 60 * 24));
  return PRESETS.filter((preset) => daysOfData >= preset.daysRequired);
}

function detectActivePreset(startDate: string, endDate: string): PresetId | null {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const now = new Date();

  // Check 48h: end is ~now, start is ~48h ago
  const hoursDiff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  if (Math.abs(hoursDiff - 48) < 1 && Math.abs(end.getTime() - now.getTime()) < 60000) {
    return '48h';
  }

  // Check 7d/30d: start at midnight, end at 23:59
  for (const preset of PRESETS) {
    if (preset.id === '48h') continue;
    const range = preset.getRange();
    const startMatch = Math.abs(start.getTime() - range.start.getTime()) < 60000;
    const endMatch = Math.abs(end.getTime() - range.end.getTime()) < 60000;
    if (startMatch && endMatch) return preset.id;
  }

  return null;
}

interface PeriodPickerProps {
  startDate: string;
  endDate: string;
  onDateChange: (start: string, end: string) => void;
  isLoading?: boolean;
}

export function PeriodPicker({ startDate, endDate, onDateChange, isLoading }: PeriodPickerProps) {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState<DayPickerDateRange | undefined>();

  const availablePresets = getAvailablePresets();
  const activePreset = detectActivePreset(startDate, endDate);
  const isCustom = activePreset === null;

  const minDate = DATA_START_DATE;
  const maxDate = new Date();

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isCalendarOpen) {
        setIsCalendarOpen(false);
        setSelectedRange(undefined);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isCalendarOpen]);

  const handlePresetClick = (preset: Preset) => {
    const range = preset.getRange();
    onDateChange(toDateTimeLocal(range.start), toDateTimeLocal(range.end));
    setIsCalendarOpen(false);
  };

  const handleCustomClick = () => {
    setIsCalendarOpen(!isCalendarOpen);
  };

  const handleRangeSelect = (range: DayPickerDateRange | undefined) => {
    setSelectedRange(range);
    if (range?.from && range?.to && range.from.getTime() !== range.to.getTime()) {
      const start = new Date(range.from);
      start.setHours(0, 0, 0, 0);
      const end = new Date(range.to);
      end.setHours(23, 59, 59, 999);
      onDateChange(toDateTimeLocal(start), toDateTimeLocal(end));
      setIsCalendarOpen(false);
      setSelectedRange(undefined);
    }
  };

  const handleCloseCalendar = () => {
    setIsCalendarOpen(false);
    setSelectedRange(undefined);
  };

  return (
    <div className="PeriodPicker">
      <div className="PeriodPicker-row">
        <div className="PeriodPicker-presets">
          {availablePresets.map((preset) => (
            <button
              key={preset.id}
              onClick={() => handlePresetClick(preset)}
              className={`PeriodPicker-preset ${activePreset === preset.id ? 'active' : ''}`}
              aria-pressed={activePreset === preset.id}
            >
              {preset.label}
            </button>
          ))}
          <button
            onClick={handleCustomClick}
            className={`PeriodPicker-preset ${isCustom ? 'active' : ''}`}
            aria-pressed={isCustom}
          >
            {isCustom ? formatDisplayRange(startDate, endDate) : 'Custom'}
          </button>
        </div>
        {isLoading && (
          <div className="PeriodPicker-loading" aria-label="Loading data">
            <div className="PeriodPicker-spinner" />
          </div>
        )}
      </div>

      {isCalendarOpen && (
        <>
          <div className="PeriodPicker-backdrop" onClick={handleCloseCalendar} />
          <div className="PeriodPicker-calendarContainer">
            <button
              className="PeriodPicker-closeButton"
              onClick={handleCloseCalendar}
              aria-label="Close calendar"
            >
              ×
            </button>
            <DayPicker
              mode="range"
              selected={selectedRange}
              onSelect={handleRangeSelect}
              disabled={{ before: minDate, after: maxDate }}
              numberOfMonths={1}
              className="PeriodPicker-calendar"
            />
          </div>
        </>
      )}
    </div>
  );
}
